import {
  convertLocationToPlaceFormat,
  convertModeToFormMode,
  convertModeToVehicleType,
} from '@/components/modules/emissions/calculator/form/utils';
import { MODE_AIR, MODE_ROAD, MODE_WATER } from '@/lib/constants';
import { LocationDictionaryModel } from '@/lib/models/location/types';
import { OptionModel } from '@/lib/models/option/types';
import { SegmentLinkModel } from '@/lib/models/segment/types';
import { CargoOption, EmissionsFormInputsInterface } from '@/types';
import { SegmentModeType } from '@/types/api-types';

import { createEmissionsSegment, extractVehicleFromSegment, getCargoItemsForMode, processStopOffs } from '../utils';

const transformRouteOptionToCalculatorFormData = ({
  option,
  locations,
  cargoParams,
}: {
  option: OptionModel;
  locations?: LocationDictionaryModel;
  cargoParams?: CargoOption;
}): EmissionsFormInputsInterface => {
  const { segments } = option;

  // Filter out transfer segments, keep only link segments (actual transportation)
  const linkSegments = segments.filter((segment): segment is SegmentLinkModel => 'toId' in segment);

  // Determine the primary mode from the segments
  const segmentModes = linkSegments.map((segment) => segment.routeType);
  let primaryMode: SegmentModeType = MODE_ROAD;
  if (segmentModes.includes(MODE_AIR)) {
    primaryMode = MODE_AIR;
  } else if (segmentModes.includes(MODE_WATER)) {
    primaryMode = MODE_WATER;
  }

  // Transform link segments to form segments
  const formSegments = linkSegments.map((segment) => {
    const vehicleType = convertModeToVehicleType(segment.routeType);

    // Get the first vehicle from the segment's vehicles array to give a more accurate result
    const firstVehicle = segment.vehicles?.[0];
    const extractedVehicleId = firstVehicle ? extractVehicleFromSegment(firstVehicle.vehicleId, segment.routeType) : '';

    // Get location data if available
    const originLocation = locations?.[segment.fromId];
    const origin = originLocation ? convertLocationToPlaceFormat(originLocation) : undefined;

    // Convert via data to stop-offs (similar to how itinerary transformer handles stopIds)
    const stopOffs = processStopOffs(option.via || [], locations || {}, true);

    return createEmissionsSegment({
      vehicleType,
      vehicleId: extractedVehicleId,
      flightNumber: '', // Route options don't seem to have flight numbers
      distance: segment.distanceKm || 0,
      origin,
      stopOffs,
    });
  });

  // Add final destination segment if needed
  if (linkSegments.length > 0) {
    const lastSegment = linkSegments[linkSegments.length - 1];
    const destinationLocation = locations?.[lastSegment.toId];
    const destinationOrigin = destinationLocation ? convertLocationToPlaceFormat(destinationLocation) : undefined;

    formSegments.push(
      createEmissionsSegment({
        vehicleType: null,
        vehicleId: '',
        flightNumber: '',
        distance: 0,
        origin: destinationOrigin,
        stopOffs: [],
      }),
    );
  }

  return {
    mode: convertModeToFormMode(primaryMode),
    cargoItems: getCargoItemsForMode(primaryMode, cargoParams),
    segments: formSegments,
  };
};

export { transformRouteOptionToCalculatorFormData };
